import { useState } from 'react';
import {
  Button,
  Card,
  ChipToggle,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import { useBranch } from '@/features/branch/branch-context';
import {
  useHomeVisitSettings,
  useSaveHomeVisitSettings,
} from '@/features/lab-packages/api';
import {
  HOME_VISIT_LIMITS as H,
  HOME_VISIT_WINDOWS,
  type HomeVisitSettings,
  type HomeVisitSettingsInput,
} from '@/features/lab-packages/types';
import styles from '@/pages/queue/queue.module.css';
import { LabFailureBox } from './LabFailureBox';
import {
  intIn,
  minToTime,
  timeToMin,
  toFailure,
  type Failure,
} from './lab-package-format';
import own from './LabPackages.module.css';

/**
 * "Home visits for blood tests" under Services & prices (RL-1138, API
 * RL-988): the fee (empty = not offered), the branches whose phlebotomist
 * goes out, the arrival windows, visits per window and the change cut-off.
 * Changes apply to new bookings; booked visits keep their fee and window.
 */
export function HomeVisitSettingsCard({ can }: { can: boolean }) {
  const q = useHomeVisitSettings();
  return (
    <Card title="Home visits for blood tests">
      {q.isLoading ? (
        <LoadingState label="Loading the home-visit settings" />
      ) : q.error || !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <SettingsForm key={JSON.stringify(q.data)} can={can} s={q.data} />
      )}
    </Card>
  );
}

type Errors = Partial<Record<string, string>>;

function SettingsForm({ can, s }: { can: boolean; s: HomeVisitSettings }) {
  const { branches } = useBranch();
  const save = useSaveHomeVisitSettings();
  const toast = useToast();
  const [fee, setFee] = useState(
    s.homeVisitFeeSen === null ? '' : senToInput(s.homeVisitFeeSen),
  );
  const [branchIds, setBranchIds] = useState(s.homeVisitBranchIds);
  const [windowMin, setWindowMin] = useState(String(s.homeVisitWindowMinutes));
  const [first, setFirst] = useState(minToTime(s.homeVisitFirstStartMin));
  const [last, setLast] = useState(minToTime(s.homeVisitLastStartMin));
  const [perWindow, setPerWindow] = useState(String(s.homeVisitsPerWindow));
  const [cutoff, setCutoff] = useState(String(s.homeVisitChangeCutoffHours));
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<Failure | null>(null);

  // Branches named by the API (one may be outside this person's list).
  const branchOptions = [
    ...branches.map((b) => ({ value: b.id, label: b.name })),
    ...(s.homeVisitBranches ?? [])
      .filter((b) => !branches.some((x) => x.id === b.id))
      .map((b) => ({ value: b.id, label: b.name })),
  ];

  function check(): HomeVisitSettingsInput | null {
    const e: Errors = {};
    const feeSen = fee.trim() ? parseRM(fee) : null;
    if (fee.trim() && (feeSen === null || feeSen > H.feeMaxSen))
      e.fee =
        'Enter the fee in RM, or leave it empty to not offer home visits.';
    const firstMin = timeToMin(first);
    const lastMin = timeToMin(last);
    if (firstMin === null) e.first = 'Enter a time, e.g. 08:00.';
    if (lastMin === null) e.last = 'Enter a time, e.g. 11:00.';
    else if (firstMin !== null && lastMin < firstMin)
      e.last = 'The last window cannot start before the first one.';
    const per = intIn(perWindow, 1, H.perWindowMax);
    if (per === null) e.perWindow = `Enter 1–${H.perWindowMax}.`;
    const cut = intIn(cutoff, H.cutoffMin, H.cutoffMax);
    if (cut === null) e.cutoff = `Enter ${H.cutoffMin}–${H.cutoffMax} hours.`;
    setErrors(e);
    if (Object.keys(e).length) return null;
    return {
      homeVisitFeeSen: feeSen,
      homeVisitBranchIds: branchIds,
      homeVisitWindowMinutes: Number(windowMin),
      homeVisitFirstStartMin: firstMin!,
      homeVisitLastStartMin: lastMin!,
      homeVisitsPerWindow: per!,
      homeVisitChangeCutoffHours: cut!,
    };
  }

  async function submit() {
    setFailure(null);
    const body = check();
    if (!body) return;
    try {
      await save.mutateAsync(body);
      toast.show({
        tone: 'success',
        title:
          body.homeVisitFeeSen === null
            ? 'Saved: home visits are not offered for new bookings'
            : `Saved: home visits ${formatRM(body.homeVisitFeeSen)} for new bookings`,
      });
    } catch (err) {
      setFailure(toFailure(err));
    }
  }

  const offered = fee.trim() !== '';
  return (
    <form
      className={styles.drawer}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {failure && <LabFailureBox failure={failure} />}
      <p className={own.note}>
        {s.homeVisitFeeSen === null
          ? 'Home visits are not offered in the app now.'
          : `Patients can book a home visit for ${formatRM(s.homeVisitFeeSen)}.`}{' '}
        Changes apply to new bookings; booked visits keep their fee and window.
      </p>
      <div className={own.grid}>
        <Field
          label="Home-visit fee (RM)"
          hint="Empty = home visits are not offered."
          error={errors.fee}
        >
          <Input
            inputMode="decimal"
            disabled={!can}
            value={fee}
            onChange={(e) => setFee(e.target.value)}
          />
        </Field>
        <Field label="Arrival window">
          <Select
            disabled={!can}
            value={windowMin}
            options={HOME_VISIT_WINDOWS.map((m) => ({
              value: String(m),
              label: `${m} minutes`,
            }))}
            onChange={(e) => setWindowMin(e.target.value)}
          />
        </Field>
        <Field
          label="First window starts"
          hint="Branch time."
          error={errors.first}
        >
          <Input
            type="time"
            disabled={!can}
            value={first}
            onChange={(e) => setFirst(e.target.value)}
          />
        </Field>
        <Field label="Last window starts" error={errors.last}>
          <Input
            type="time"
            disabled={!can}
            value={last}
            onChange={(e) => setLast(e.target.value)}
          />
        </Field>
        <Field
          label="Visits per window"
          hint="Home visits one branch can do in the same window."
          error={errors.perWindow}
        >
          <Input
            inputMode="numeric"
            disabled={!can}
            value={perWindow}
            onChange={(e) => setPerWindow(e.target.value)}
          />
        </Field>
        <Field
          label="Change cut-off (hours)"
          hint="Hours before the window after which the app can no longer book, move or cancel."
          error={errors.cutoff}
        >
          <Input
            inputMode="numeric"
            disabled={!can}
            value={cutoff}
            onChange={(e) => setCutoff(e.target.value)}
          />
        </Field>
      </div>
      <ChipToggle
        label="Branches whose phlebotomist does home visits"
        options={branchOptions}
        selected={branchIds}
        disabled={!can}
        onChange={setBranchIds}
      />
      {offered && branchIds.length === 0 && (
        <p className={styles.warnNote} role="status">
          No branch is chosen, so patients cannot book a home visit yet.
        </p>
      )}
      {can && (
        <div>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Save home-visit settings
          </Button>
        </div>
      )}
    </form>
  );
}
